/**
 * Tiny client-side JWT claim reader. NOT a verifier — the server
 * already verified the token before issuing it; we just decode the
 * payload to read the `role` claim for routing decisions.
 *
 * If you find yourself reaching for this for anything other than
 * routing/UI, stop and add a server endpoint instead. Trusting JWT
 * payload contents on the client without server verification is
 * fine for "which sidebar do I render"; not fine for "can this user
 * approve payroll" (that's enforced server-side via requireRole).
 */

type Role = 'owner' | 'admin' | 'hr' | 'finance' | 'employee' | 'it';
const ROLES: ReadonlySet<Role> = new Set([
  'owner',
  'admin',
  'hr',
  'finance',
  'employee',
]);

/**
 * Pull the `role` claim out of a JWT access token. Returns null if
 * the token is malformed or the role isn't one of Kora's known
 * values (defense against weird payloads).
 */
export function decodeJwtRoleClaim(token: string): Role | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  const payload = parts[1];
  if (!payload) return null;
  try {
    // base64url → base64 → JSON
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '=',
    );
    const json = atob(padded);
    const obj = JSON.parse(json) as { role?: unknown };
    if (typeof obj.role !== 'string') return null;
    return ROLES.has(obj.role as Role) ? (obj.role as Role) : null;
  } catch {
    return null;
  }
}
